/**
 * Fellido · Geschenk-Auswahl
 *
 * Bewusst KEIN eigenes Warenkorb-System. Ablauf:
 *   1. Klick auf "In den Warenkorb" wird einmalig abgefangen.
 *   2. Dialog oeffnet, Kunde waehlt die Variante seines Geschenks.
 *   3. Das Geschenk wird still per /cart/add.js hinzugefuegt.
 *   4. Der urspruengliche Klick wird wiederholt — ab da macht Horizons
 *      eigenes Produktformular alles Weitere: Hauptprodukt hinzufuegen,
 *      Sections aktualisieren, Drawer oeffnen.
 *
 * Faellt etwas aus, geht der Kauf trotzdem durch (fail-open).
 */
class FGiftPicker extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.giftHandle = this.dataset.giftHandle;
    this.property = this.dataset.property || '_geschenk';
    this.pendingTrigger = null;
    this.busy = false;

    this.addEventListener('click', this.onClick);
    this.dialog.addEventListener('cancel', () => this.reset());
    document.addEventListener('click', this.onDocumentClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick, true);
  }

  /** Merkt sich, ob dieser Klick schon durchgelassen wurde. */
  onDocumentClick = (event) => {
    const trigger = event.target.closest('[on\\:click], button[type="submit"], .add-to-cart-button, add-to-cart-component button, [data-f-bundle-add]');
    if (!trigger) return;
    if (trigger.dataset.fGiftDone === 'true') return;
    if (this.closest('dialog')?.contains(trigger)) return;

    // Die Mengenstaffel hat einen eigenen Warenkorb-Button ausserhalb jedes
    // Formulars. Ohne diese Ausnahme kaeme beim Kauf von zwei Haeusern gar
    // kein Geschenk in den Warenkorb.
    const eigenerKnopf = trigger.matches('[data-f-bundle-add]');
    const form = trigger.closest('form[action*="/cart/add"]');
    if (!form && !eigenerKnopf) return;

    // Nur der echte Warenkorb-Button, nicht Express-Checkout-Buttons
    if (trigger.closest('.shopify-payment-button, accelerated-checkout-component')) return;

    event.preventDefault();
    event.stopPropagation();
    this.pendingTrigger = trigger;
    this.open();
  };

  onClick = (event) => {
    if (event.target.closest('[data-gift-close]')) {
      this.reset();
      this.dialog.close();
      return;
    }
    if (event.target.closest('[data-gift-skip]')) {
      this.continueWithoutGift();
      return;
    }
    if (event.target.closest('[data-gift-confirm]')) {
      this.confirm();
    }
  };

  open() {
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    this.querySelector('input[type="radio"]:checked')?.focus();
  }

  reset() {
    this.pendingTrigger = null;
    this.busy = false;
  }

  /** Den urspruenglichen Klick wiederholen — Horizon uebernimmt ab hier. */
  handOverToTheme() {
    const trigger = this.pendingTrigger;
    this.pendingTrigger = null;
    this.dialog.close();
    if (!trigger) return;
    trigger.dataset.fGiftDone = 'true';
    trigger.click();
    // Fuer den naechsten Kauf wieder scharf schalten
    requestAnimationFrame(() => delete trigger.dataset.fGiftDone);
  }

  continueWithoutGift() {
    this.handOverToTheme();
  }

  async confirm() {
    if (this.busy) return;
    const selected = this.querySelector('input[type="radio"]:checked');
    if (!selected) return;

    this.busy = true;
    const button = this.querySelector('[data-gift-confirm]');
    const label = button?.textContent;
    if (button) {
      button.disabled = true;
      button.textContent = '…';
    }

    try {
      // Liegt dieses Set schon im Warenkorb, kein zweites anlegen.
      // Sonst zahlt der Kunde eines davon: Shopifys Gratis-Rabatt gilt
      // nur einmal pro Bestellung (usesPerOrderLimit).
      try {
        const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        })).json();
        const schonDrin = (cart.items || []).some((i) => i.variant_id === Number(selected.value));
        if (schonDrin) return;
      } catch (e) {
        // Warenkorb nicht lesbar: lieber hinzufuegen als das Geschenk verlieren.
      }

      const body = {
        items: [
          {
            id: Number(selected.value),
            quantity: 1,
            properties: { [this.property]: 'Geschenk' },
          },
        ],
      };
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(`cart/add ${response.status}`);
    } catch (error) {
      // Der Kauf darf nie an der Geschenkzeile scheitern.
      console.warn('[Fellido] Geschenk konnte nicht hinzugefügt werden:', error);
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = label;
      }
      this.busy = false;
      this.handOverToTheme();
    }
  }
}

if (!customElements.get('f-gift-picker')) {
  if (!customElements.get('f-gift-picker')) customElements.define('f-gift-picker', FGiftPicker);
}
